/*! event-target.js.js */

const COMMA = /\s*,\s*/g;
const LISTENERS = Symbol("listeners");
const PARENT = Symbol("parent");
/**
 * @class EventTarget
 */
class EventTarget {
    /**
     * @constructs EventTarget
     */
    constructor() {
        this[LISTENERS] = {};
        this[PARENT] = null;
    }

    /**
     * @method
     * @param {string} eventType
     * @param {function} eventHandler
     * @param {boolean} [deRegister] Flag to remove listener on first dispatch
     */
    addEventListener(eventType, eventHandler, deRegister = false) {
        if (!assertTypeAndHandler(eventType, eventHandler)) {
            return;
        }

        if (!(this[LISTENERS][eventType])) {
            this[LISTENERS][eventType] = [];
        }

        this[LISTENERS][eventType].push({ callback: eventHandler, deRegister });
    }

    /**
     * @method
     * @param {string} eventType
     * @param {function} eventHandler
     * @returns {*}
     */
    removeEventListener(eventType, eventHandler) {
        if (!assertTypeAndHandler(eventType, eventHandler)) {
            return;
        }

        const stack = this[LISTENERS][eventType];

        if (!stack) {
            return;
        }

        if (!eventHandler) {
            stack.length = 0;
            return;
        }

        for (let i = 0, l = stack.length; i < l; i++) {
            if (stack[i].callback === eventHandler) {
                stack.splice(i, 1);
                return this.removeEventListener(eventType, eventHandler);
            }
        }
    }

    /**
     * @method
     */
    removeAllEventListeners() {
        if (this[LISTENERS]) {
            Object.keys(this[LISTENERS]).forEach((eventType) => {
                this[LISTENERS][eventType].length = 0;
            });
        }
    }

    /**
     * @method
     * @param {Event} event
     */
    dispatchEvent(event) {
        if (!this[LISTENERS]) {
            return;
        }

        const eventType = event.type;
        if (this[LISTENERS].hasOwnProperty(eventType)) {
            const stack = this[LISTENERS][eventType];
            const listenersToRemove = [];

            Object.defineProperty(event, "target", { value: this, configurable: true });
            for (let i = 0, l = stack.length; i < l; i++) {
                const callback = stack[i].callback;
                const deRegister = stack[i].deRegister;

                callback.call(this, event);
                if (deRegister) {
                    listenersToRemove.push(callback);
                }
            }

            listenersToRemove.forEach(callback => this.removeEventListener(eventType, callback));
        }

        bubbleEvent(this[PARENT], event, this);
    }

    /**
     * Sets event parent for events to bubble to
     * @param {EventTarget} eventTarget
     */
    setEventParent(eventTarget) {
        if (!(eventTarget instanceof EventTarget)) {
            return;
        }
        this[PARENT] = eventTarget;
    }

    /**
     * @method
     * @param {string} eventTypes One or more comma-delimited event types
     * @param {function} eventHandler
     * @returns {EventTarget}
     */
    on(eventTypes, eventHandler) {
        if (!assertTypeAndHandler(eventTypes, eventHandler)) {
            return this;
        }

        const eventTypeTokens = eventTypes.split(COMMA);
        eventTypeTokens.forEach((type) => {
            this.addEventListener(type, eventHandler);
        });
        return this;
    }

    /**
     * Binds event handler which executes only once and then removes itself
     * @param {string} eventTypes One or more comma-delimited event types
     * @param {function} eventHandler
     * @returns {EventTarget}
     */
    one(eventTypes, eventHandler) {
        if (!assertTypeAndHandler(eventTypes, eventHandler)) {
            return this;
        }

        const eventTypeTokens = eventTypes.split(COMMA);
        eventTypeTokens.forEach((type) => {
            this.addEventListener(type, eventHandler, true);
        });
        return this;
    }

    /**
     * @param {string} eventTypes One or more comma-delimited event types
     * @param {function} eventHandler
     * @returns {EventTarget}
     */
    off(eventTypes, eventHandler) {
        if (!assertTypeAndHandler(eventTypes, eventHandler)) {
            return this;
        }

        const eventTypeTokens = eventTypes.split(COMMA);
        eventTypeTokens.forEach((type) => {
            this.removeEventListener(type, eventHandler);
        });
        return this;
    }

    trigger(eventType: string, data?: any): EventTarget {
        if (!assertType(eventType)) {
            return this;
        }
        const eventInitDict = {
            detail: data,
            bubbles: true,
            originalTarget: this,
            target: this
        };
        const event = new CustomEvent(eventType, eventInitDict);
        this.dispatchEvent(event);
        return this;
    }

    /**
     * @method
     */
    dispose() {
        this.removeAllEventListeners();
        this[LISTENERS] = null;
        this[PARENT] = null;
    }
}

/**
 * @private
 * @param {EventTarget} parent
 * @param {Event} event
 * @param {EventTarget} originalTarget
 */
function bubbleEvent(parent, event, originalTarget) {
    if (parent && event.bubbles && !event.defaultPrevented) {
        if (!event.originalTarget) {
            Object.defineProperty(event, "originalTarget", { value: originalTarget });
        }
        parent.dispatchEvent(event);
    }
}

/**
 * @private
 * @param {string} eventType
 * @param {function} eventHandler
 * @returns {boolean}
 */
function assertTypeAndHandler(eventType, eventHandler) {
    return assertType(eventType) && eventHandler && (typeof eventHandler === "function");
}

/**
 * @private
 * @param {string} eventType
 * @returns {boolean}
 */
function assertType(eventType) {
    return eventType && (typeof eventType === "string");
}

export default EventTarget;
